import React, { useEffect, useState } from "react";
import AdminSidebar from "../../components/AdminSidebar";
import "./admin.css";
import "./FMSDashboard.css";

export default function AdminMaintenanceView() {
  const [issues, setIssues] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [error, setError] = useState("");
  const [selectedTower, setSelectedTower] = useState("");
  const [selectedFloor, setSelectedFloor] = useState("");
  const [towers, setTowers] = useState([]);
  const [floors, setFloors] = useState([]);

  const API = process.env.REACT_APP_API_URL;
  const adminRole = localStorage.getItem("adminRole");
  const adminTower = localStorage.getItem("adminTower");

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/maintenance/admin`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (!res.ok) {
          setError("Failed to load issues");
          return;
        }
        let data = await res.json();

        // If tower admin, filter to only their tower
        if (adminRole !== "admin" && adminTower) {
          data = data.filter((i) => i.tower === adminTower);
          setSelectedTower(adminTower);
        }

        setIssues(data);
        setFilteredIssues(data);

        // Extract unique towers and floors
        const uniqueTowers = [...new Set(data.map((i) => i.tower))].sort();
        setTowers(uniqueTowers);
      } catch (err) {
        setError("Server error while loading issues");
      }
    };
    load();
  }, [API, adminRole, adminTower]);

  // Update filtered issues when filters change
  useEffect(() => {
    let filtered = issues;

    // For super admins: filter by selected tower first
    if (adminRole === "admin" && selectedTower) {
      filtered = filtered.filter((i) => i.tower === selectedTower);
    }

    // Extract floors based on filtered issues (for both super admin and tower admin)
    if (filtered.length > 0) {
      const uniqueFloors = [...new Set(filtered.map((i) => {
        const match = i.unitNumber?.match(/^[A-Z]+-(\d+)/);
        return match ? match[1] : "Unknown";
      }))].sort((a, b) => {
        if (a === "Unknown") return 1;
        return parseInt(a) - parseInt(b);
      });
      setFloors(uniqueFloors);
    } else {
      setFloors([]);
    }

    // Filter by selected floor if any
    if (selectedFloor) {
      filtered = filtered.filter((i) => {
        const match = i.unitNumber?.match(/^[A-Z]+-(\d+)/);
        return match ? match[1] === selectedFloor : false;
      });
    }

    setFilteredIssues(filtered);
  }, [selectedTower, selectedFloor, issues, adminRole]);

  return (
    <div className="admin-layout">
      <AdminSidebar />

      <div className="admin-content">
        <h1>Maintenance Issues</h1>
        <p className="admin-sub">Read-only visibility into resident-reported issues and their status</p>

        {error && <p className="admin-error">{error}</p>}

        {/* Filters */}
        {adminRole === "admin" && (
          <div className="maintenance-filters">
            <div className="filter-group">
              <label>
                Tower:
                <select
                  value={selectedTower}
                  onChange={(e) => {
                    setSelectedTower(e.target.value);
                    setSelectedFloor("");
                  }}
                >
                  <option value="">All Towers</option>
                  {towers.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {selectedTower && (
              <div className="filter-group">
                <label>
                  Floor:
                  <select value={selectedFloor} onChange={(e) => setSelectedFloor(e.target.value)}>
                    <option value="">All Floors</option>
                    {floors.map((f) => (
                      <option key={f} value={f}>
                        Floor {f}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          </div>
        )}

        {/* Floor filter for tower admins */}
        {adminRole !== "admin" && adminTower && (
          <div className="maintenance-filters">
            <div className="filter-group">
              <label>
                Floor:
                <select value={selectedFloor} onChange={(e) => setSelectedFloor(e.target.value)}>
                  <option value="">All Floors</option>
                  {floors.map((f) => (
                    <option key={f} value={f}>
                      Floor {f}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        )}

        <div className="fms-list">
          {filteredIssues.length > 0 && (
            <p className="filter-result-count">
              Showing {filteredIssues.length} issue{filteredIssues.length !== 1 ? "s" : ""}
            </p>
          )}
          {filteredIssues.map((i) => (
            <div className="fms-item" key={i.id}>
              <div className="fms-item-header">
                <span className="fms-item-category">{i.category}</span>
                <span className="fms-item-location">{i.tower} · {i.unitNumber}</span>
              </div>
              <div className="fms-item-desc">{i.description}</div>
              <div className="fms-item-meta">
                Reported by {i.residentName} on {new Date(i.createdAt).toLocaleString()}
              </div>
              {i.acknowledgedBy && (
                <div className="fms-item-meta">
                  Acknowledged by {i.acknowledgedBy.name} ({i.acknowledgedBy.email})
                </div>
              )}
              {i.updatedBy && (
                <div className="fms-item-meta">
                  Last updated by {i.updatedBy.name} ({i.updatedBy.email}) — {new Date(i.updatedAt).toLocaleString()}
                </div>
              )}
              <span className={`status-badge status-${i.status.replace(/\s/g, "")}`}>{i.status}</span>
            </div>
          ))}

          {filteredIssues.length === 0 && !error && (
            <p className="admin-empty">
              {issues.length === 0 ? "No issues reported yet" : "No issues found for the selected filters"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}